import { datadogLogs } from "@datadog/browser-logs";
import ky from "ky";
import * as React from "react";
import { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { NavLink } from "react-router-dom";

import { apiErrorToMessage, getStandardHeaders, parseError } from "@/apiUtils";
import { CommandBlock } from "@/components/system/command-tabs";
import { InputHook } from "@/components/system/input-hook";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Paragraph } from "@/components/ui/typography";
import { Urls } from "@/domain/urls";
import { ApiToken } from "@/pages/Profile/types";
import { LoadState } from "@/store";

interface CreateTokenFormProps {
  open: boolean;
  handleClose: () => void;
  formTitle: string;
  handleSuccess: (token: ApiToken) => void;
}

interface CreateTokenFormInput {
  label?: string;
  daysTtl?: string;
}

type ApiTokenWithKey = ApiToken & { token: string };

type SubmitState =
  | { type: "none" }
  | { type: "done"; token: ApiTokenWithKey }
  | { type: "submitting" }
  | { type: "error"; error: string };

const createToken = async (
  label?: string,
  daysTtl?: string,
): Promise<LoadState<ApiTokenWithKey>> => {
  const daysToExpiry = (days: string): string => {
    const d = new Date();
    d.setDate(d.getDate() + parseInt(days, 10));
    return d.toISOString();
  };

  const expiryObj = daysTtl ? { expiry: daysToExpiry(daysTtl) } : {};
  const labelObj = label ? { label } : {};
  const json = { ...expiryObj, ...labelObj };

  try {
    const response = (await ky
      .post(`/api/v1/api-tokens/`, {
        json,
        headers: getStandardHeaders(),
      })
      .json()) as ApiTokenWithKey;
    return { type: "loaded", data: response };
  } catch (err) {
    datadogLogs.logger.error("Error creating token");
    return { type: "error", error: parseError(err) };
  }
};

interface LoginCodeProps {
  token: string;
}

const LoginCode = ({ token }: LoginCodeProps): React.ReactElement => {
  return (
    <div className="flex flex-col gap-2">
      <p>In a terminal:</p>
      <CommandBlock
        command={`coiled login --token ${token}`}
        showTerminalIcon
      />
    </div>
  );
};

interface ShowGeneratedTokenProps {
  token: string;
  handleDialogClose: () => void;
}

const ShowGeneratedToken = ({
  token,
  handleDialogClose,
}: ShowGeneratedTokenProps): React.ReactElement => {
  return (
    <div className="flex flex-col gap-2">
      <Alert variant="info">
        <AlertDescription>
          Make sure to copy your personal access token now. You won&apos;t be
          able to see it again!
        </AlertDescription>
      </Alert>
      <p>Your token is:</p>
      <CommandBlock command={token} />
      <LoginCode token={token} />
      <p>
        You can manage your API tokens on the{" "}
        <NavLink
          to={`/${Urls.Profile}`}
          onClick={handleDialogClose}
          className="text-primary underline hover:text-primary/80"
        >
          Profile
        </NavLink>{" "}
        page.
      </p>
    </div>
  );
};

export const FormCreateApiToken = ({
  handleClose,
  open,
  formTitle,
  handleSuccess,
}: CreateTokenFormProps): React.ReactElement => {
  const [submissionState, setSubmissionState] = useState<SubmitState>({
    type: "none",
  });

  const methods = useForm<CreateTokenFormInput>();

  // Reset form when dialog opens
  React.useEffect(() => {
    if (open) {
      methods.reset();
    }
  }, [open, methods]);

  const onSubmit = async (data: CreateTokenFormInput) => {
    setSubmissionState({ type: "submitting" });
    const result = await createToken(
      data.label || undefined,
      data.daysTtl || undefined,
    );
    if (result.type === "error") {
      setSubmissionState({
        type: "error",
        error: apiErrorToMessage(result.error),
      });
    } else if (result.type === "loaded") {
      setSubmissionState((oldState) =>
        // only update to "done" if old state was "submitting"
        // this avoids an update to "done" if e.g. the user closed the dialog box during the request
        // (which would bring the dialog box back up, a bad experience)
        oldState.type === "submitting"
          ? { type: "done", token: result.data }
          : oldState,
      );

      // Add new token (without the secret token) to make sure user sees it right away
      //
      // (throwaway variable "_" is unused; needed for removing the "token" key)
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { token: _, ...tokenWithOutSecret } = result.data;
      handleSuccess(tokenWithOutSecret);
    } else {
      datadogLogs.logger.error(
        "Only expected result type 'loaded' or 'error'. Found: " + result.type,
      );
      setSubmissionState({
        type: "error",
        error: apiErrorToMessage("Error creating token."),
      });
    }
  };

  const handleDialogClose = (): void => {
    setSubmissionState({ type: "none" });
    handleClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleDialogClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{formTitle}</DialogTitle>
        </DialogHeader>
        {submissionState.type === "done" && (
          <ShowGeneratedToken
            handleDialogClose={handleDialogClose}
            token={submissionState.token.token}
          />
        )}
        {submissionState.type !== "done" && (
          <FormProvider {...methods}>
            <form
              onSubmit={methods.handleSubmit(onSubmit)}
              className="flex flex-col gap-4"
            >
              <Paragraph variant="muted">All fields are optional.</Paragraph>
              <div className="flex flex-col gap-4">
                <InputHook
                  name="label"
                  label="Label"
                  placeholder="Enter a label for this token"
                />
                <InputHook
                  name="daysTtl"
                  label="Days to expiration (default: never)"
                  type="number"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  placeholder="Enter number of days"
                />
              </div>
              <Paragraph variant="muted">
                You will be able to revoke this token at any time from the{" "}
                <NavLink
                  to={`/${Urls.Profile}`}
                  onClick={handleDialogClose}
                  className="text-primary underline hover:text-primary/80"
                >
                  Profile
                </NavLink>{" "}
                page.
              </Paragraph>
              {submissionState.type === "error" && (
                <Alert variant="destructive">
                  <AlertDescription>{submissionState.error}</AlertDescription>
                </Alert>
              )}
              <div className="flex justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleDialogClose}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={submissionState.type === "submitting"}
                  className="flex-1"
                >
                  {submissionState.type === "submitting"
                    ? "Creating..."
                    : "Create"}
                </Button>
              </div>
            </form>
          </FormProvider>
        )}
      </DialogContent>
    </Dialog>
  );
};
